import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Switch,
} from "@mui/material";
import Icon from "@mdi/react";
import { mdiCalendarPlus, mdiCashMultiple, mdiDoorOpen, mdiGiftOutline, mdiLinkVariant } from "@mdi/js";
import { useNavigate } from "react-router-dom";

import { GroupRole, GroupRoles } from "api/group";

type Screen = "actions" | "roles";

export interface GroupUser {
  uid: number;
  name: string | null;
  avatar: string | null;
  admin?: boolean;
  tag: string | null;
}

export interface UserActionItem {
  handler: () => Promise<void>;
  label: string;
}

export interface UserActionsDialogProps {
  actions: UserActionItem[];
  onClose: () => void;
  onRoleChange: (role: GroupRole, checked: boolean) => Promise<void>;
  open: boolean;
  roles: GroupRoles;
  showManageRoles: boolean;
  user: GroupUser | null;
}

const ROLE_LABELS: Record<GroupRole, string> = {
  "manage-events": "Events",
  "manage-rooms": "Rooms",
  "manage-money": "Money",
  "manage-secret-santa": "Secret Santa",
  "manage-invite-link": "Invite link",
};

const ROLE_ICONS: Record<GroupRole, string> = {
  "manage-events": mdiCalendarPlus,
  "manage-rooms": mdiDoorOpen,
  "manage-money": mdiCashMultiple,
  "manage-secret-santa": mdiGiftOutline,
  "manage-invite-link": mdiLinkVariant,
};

const ALL_ROLES: GroupRole[] = [
  "manage-events",
  "manage-rooms",
  "manage-money",
  "manage-secret-santa",
  "manage-invite-link",
];

function UserActionsDialog(props: UserActionsDialogProps) {
  const { actions, onClose, onRoleChange, open, roles, showManageRoles, user } = props;
  const [screen, setScreen] = useState<Screen>("actions");
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setScreen("actions");
    }
  }, [open]);

  async function handleActionClick(handler: () => Promise<void>): Promise<void> {
    await handler();
    onClose();
  }

  return (
    <Dialog maxWidth='xs' onClose={() => navigate(-1)} open={open}>
      {screen === "actions" ? (
        <List disablePadding>
          {actions.map((action) => (
            <ListItemButton key={action.label} onClick={() => handleActionClick(action.handler)}>
              <ListItemText primary={action.label} />
            </ListItemButton>
          ))}
          {showManageRoles && (
            <ListItemButton onClick={() => setScreen("roles")}>
              <ListItemText primary='Manage roles' />
            </ListItemButton>
          )}
        </List>
      ) : (
        <>
          <DialogTitle>{user?.name ?? "User"} can manage</DialogTitle>
          <DialogContent>
            <List disablePadding sx={{ minWidth: 280 }}>
              {ALL_ROLES.map((role) => {
                const checked = Boolean(roles[role]?.includes(user?.uid ?? -1));
                return (
                  <ListItem key={role}>
                    <ListItemIcon>
                      <Icon path={ROLE_ICONS[role]} size={1} />
                    </ListItemIcon>
                    <ListItemText primary={ROLE_LABELS[role]} />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={checked}
                        edge='end'
                        onChange={(_, value) => onRoleChange(role, value)}
                        inputProps={{ "aria-label": ROLE_LABELS[role] }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

export default UserActionsDialog;
